/**
 * src/utils/revenue.ts
 *
 * THE single source of truth for revenue math in the frontend.
 * Mirrors patient-history-api/services/revenueService.js exactly.
 *
 * Cash-basis contract (locked, user-confirmed):
 *
 *   For each Billing b:
 *     paid                → +abs(b.amountPaid || b.totalAmount)
 *     partial             → +abs(b.amountPaid)             (only the paid portion)
 *     partially_refunded  → +max(0, amountPaid - amountRefunded)
 *     refund_due          → +abs(b.amountPaid)             (cash still in till)
 *     refunded (new)      → +max(0, amountPaid - amountRefunded)
 *     refunded (legacy)   → -abs(b.totalAmount)            (no amountRefunded recorded)
 *     pending / cancelled → 0
 *
 * Used by RevenueAnalytics.tsx, ClinicRevenueOverview.tsx, and the daily-earned
 * indicator in DashboardHeader.tsx. No component is allowed to redefine STATUS_CONFIG
 * or per-billing net math anywhere else.
 */

import type { Billing } from '../components/type';

export type RevenueStatus =
    | 'paid'
    | 'partial'
    | 'partially_refunded'
    | 'refunded'
    | 'refund_due'
    | 'pending'
    | 'cancelled';

interface StatusInfo {
    sign: 1 | -1 | 0;
    color: string;
    label: { ar: string; en: string };
}

export const STATUS_CONFIG: Record<RevenueStatus, StatusInfo> = {
    paid:               { sign:  1, color: '#52c41a', label: { ar: 'مدفوع',           en: 'Paid' } },
    partial:            { sign:  1, color: '#1890ff', label: { ar: 'جزئي',            en: 'Partial' } },
    partially_refunded: { sign:  1, color: '#fa8c16', label: { ar: 'استرداد جزئي',    en: 'Partially refunded' } },
    // refund_due: refund approved but money still physically in till — counts +amountPaid in BE.
    refund_due:         { sign:  1, color: '#fa8c16', label: { ar: 'استرداد معلق',    en: 'Refund pending' } },
    refunded:           { sign: -1, color: '#f5222d', label: { ar: 'مُسترد',          en: 'Refunded' } },
    pending:            { sign:  0, color: '#8c8c8c', label: { ar: 'معلق',             en: 'Pending' } },
    cancelled:          { sign:  0, color: '#bfbfbf', label: { ar: 'ملغي',             en: 'Cancelled' } },
};

/**
 * Per-document signed net contribution. Pure function. Mirrors backend `netFor`
 * in patient-history-api/services/revenueService.js byte-for-byte.
 */
export function computeBillingNet(
    b: Pick<Billing, 'paymentStatus' | 'totalAmount' | 'amountPaid'> & { amountRefunded?: number }
): number {
    if (!b || !b.paymentStatus) return 0;
    const status = b.paymentStatus as RevenueStatus;
    const paid     = Number(b.amountPaid)     || 0;
    const refunded = Number((b as any).amountRefunded) || 0;
    const total    = Number(b.totalAmount)    || 0;

    if (status === 'paid') {
        // Legacy fallback: pre-amountPaid rows had paid=0 but bill was paid.
        return Math.abs(paid || total);
    }
    if (status === 'partial') {
        return paid > 0 ? paid : 0;
    }
    if (status === 'refund_due') {
        // Refund approved but not physically returned yet — money is still in the till.
        return paid > 0 ? paid : 0;
    }
    if (status === 'partially_refunded') {
        return Math.max(0, paid - refunded);
    }
    if (status === 'refunded') {
        if (refunded > 0) return Math.max(0, paid - refunded); // typically 0 for full refund
        // Legacy rows where amountRefunded was never written: keep old contract
        // so historical totals stay stable until the migration script backfills.
        return -Math.abs(total);
    }
    return 0;
}

export interface RevenueAggregate {
    totalRevenue: number;
    amountCollected: number;
    refundedAmount: number;
    pendingAmount: number;
    billingCount: number;
    visitCount?: number;
    recordCount?: number;
}

/**
 * Per-document refund amount (always >= 0). Mirrors backend `refundFor`.
 * Used so refundedAmount is computed independent of net (avoids double-counting partials).
 */
export function computeBillingRefund(
    b: Pick<Billing, 'paymentStatus' | 'totalAmount' | 'amountPaid'> & { amountRefunded?: number }
): number {
    if (!b || !b.paymentStatus) return 0;
    const status  = b.paymentStatus as RevenueStatus;
    const refunded = Number((b as any).amountRefunded) || 0;
    if (status === 'partially_refunded') return Math.max(0, refunded);
    if (status === 'refunded') {
        if (refunded > 0) return refunded;
        const paid  = Number(b.amountPaid)  || 0;
        const total = Number(b.totalAmount) || 0;
        return Math.max(0, paid || total);
    }
    return 0;
}

/**
 * Pure aggregate over a list of billings. Mirrors backend `aggregateRevenue`.
 */
export function aggregateBillings(
    rows: Array<Pick<Billing, 'paymentStatus' | 'totalAmount' | 'amountPaid'> & { amountRefunded?: number }>
): RevenueAggregate {
    let totalRevenue = 0;
    let amountCollected = 0;
    let refundedAmount = 0;
    let pendingAmount = 0;

    for (const b of rows) {
        const n = computeBillingNet(b);
        totalRevenue += n;

        const status = b.paymentStatus as RevenueStatus;
        const paid = Number(b.amountPaid) || 0;
        if (status === 'paid' || status === 'partial' || status === 'refund_due' ||
            status === 'partially_refunded' || status === 'refunded') {
            amountCollected += paid || (status === 'paid' ? (Number(b.totalAmount) || 0) : 0);
        }
        refundedAmount += computeBillingRefund(b);

        if (status === 'pending' || status === 'partial') {
            const total = Number(b.totalAmount) || 0;
            pendingAmount += Math.max(0, total - paid);
        }
    }

    return { totalRevenue, amountCollected, refundedAmount, pendingAmount, billingCount: rows.length };
}

/**
 * Canonical response from the new /api/revenue/* endpoints.
 */
export interface RevenuePayload extends RevenueAggregate {
    period: { startDate: string; endDate: string };
    breakdown?: Array<RevenueAggregate & { doctor_id: string; doctor_name?: string }>;
}

/**
 * Convenience: get the user-facing label/color for a payment status.
 */
export function statusInfo(status: string | undefined | null, lang: 'ar' | 'en' = 'ar'): { sign: 1 | -1 | 0; color: string; label: string } {
    const cfg = STATUS_CONFIG[(status || 'pending') as RevenueStatus] ?? STATUS_CONFIG.pending;
    return { sign: cfg.sign, color: cfg.color, label: cfg.label[lang] };
}
